// Direction B "Ink Header" thread.
// Layout: 18px×16px×14px padding, gap 10px between rows, 6px thread-spacer
// between groups from different senders, white bg. Scrollbar hidden via
// scroll-bar-w-0 utility classes (the panel handles overflow visually
// through clipping).
//
// Auto-scrolls to the bottom on every new message or status change. Renders
// the block-variant TypingDots while we are submitted/streaming and the last
// message isn't yet an assistant turn — this is the TTFT gap.
//
// The thread also emits a per-group meta line ("MIKLA · REPLIED IN 47s") under
// every assistant group when the assistant message has a `_meta` hint set.
// For v1 we render a generic "MIKLA · AI AGENT" caption under each assistant
// group as a stand-in; richer per-turn meta (response latency etc.) is a
// follow-up that needs server-side timing data.

import { useEffect, useRef } from 'react'
import type { UIMessage } from 'ai'
import { TextBubble } from './TextBubble'
import { TypingDots } from './TypingDots'
import { AssistantTurn, type AssistantPart } from './AssistantTurn'
import { useVendorInfo, getAgentName } from '../context/VendorInfoContext'

export interface MessageListProps {
  messages: UIMessage[]
  status: 'ready' | 'submitted' | 'streaming' | 'error'
  pendingUserText?: string | null
}

interface RuntimeMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  parts?: AssistantPart[]
  content?: string
}

export function MessageList({ messages, status, pendingUserText }: MessageListProps) {
  const endRef = useRef<HTMLDivElement>(null)
  // Per-group meta line ("● {agentName} · AI agent") under each assistant
  // group reads the resolved name from VendorInfoContext (App provides this
  // via VendorInfoProvider once /chat/session resolves). When the message
  // list mounts inside the launcher path before context is available, the
  // hook returns a sensible default which getAgentName promotes to "Mikla".
  const agentName = getAgentName(useVendorInfo())
  const groupMeta = `${agentName} · AI agent`

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, status, pendingUserText])

  const last = messages[messages.length - 1] as RuntimeMessage | undefined
  const showTypingDots =
    (status === 'submitted' || status === 'streaming') &&
    last?.role !== 'assistant' &&
    !pendingUserText

  return (
    <div
      className="flex flex-col gap-2.5 overflow-y-auto px-4 pt-[18px] pb-3.5 bg-paper [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      data-testid="message-list"
      role="log"
      aria-live="polite"
    >
      {messages.map((m, idx) => {
        const rm = m as unknown as RuntimeMessage
        const prev = idx > 0 ? (messages[idx - 1] as unknown as RuntimeMessage) : null
        const senderChanged = prev !== null && prev.role !== rm.role
        const spacer = senderChanged ? <div key={`spacer-${rm.id}`} className="h-1.5" /> : null

        if (rm.role === 'user') {
          const text =
            rm.parts?.find((p) => p.type === 'text')?.text || rm.content || ''
          return (
            <Group key={rm.id} from="user" prefix={spacer}>
              <TextBubble role="user">{text}</TextBubble>
            </Group>
          )
        }
        return (
          <Group key={rm.id} from="agent" prefix={spacer} meta={groupMeta}>
            <AssistantTurn id={rm.id} parts={rm.parts || []} />
          </Group>
        )
      })}
      {pendingUserText && (
        <Group from="user" key="__pending__">
          <TextBubble role="user">{pendingUserText}</TextBubble>
        </Group>
      )}
      {showTypingDots && (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-end gap-2 max-w-full">
            {/* Match avatar slot width so the typing bubble lines up with text bubbles */}
            <div className="w-7 flex-shrink-0" />
            <TypingDots variant="block" />
          </div>
          {/* Meta line below the typing dots — same mono uppercase shape as the
              per-group meta line, padded to align with the bubble after the
              avatar slot. Uses the resolved agent name (e.g. "MIKLA IS TYPING…"). */}
          <div className="font-mono text-[10px] uppercase tracking-eyebrow text-muted-400 pl-9">
            {`${agentName} is typing…`}
          </div>
        </div>
      )}
      <div ref={endRef} />
    </div>
  )
}

interface GroupProps {
  from: 'user' | 'agent'
  meta?: string
  prefix?: React.ReactNode
  children: React.ReactNode
}

function Group({ from, meta, prefix, children }: GroupProps) {
  return (
    <>
      {prefix}
      <div className={from === 'user' ? 'flex flex-col items-end gap-2.5' : 'flex flex-col gap-2.5'}>
        {children}
        {meta && (
          <div
            className={[
              'font-mono text-[10px] uppercase tracking-eyebrow text-muted-400',
              from === 'agent' ? 'pl-9' : 'pr-1 text-right',
            ].join(' ')}
          >
            {meta}
          </div>
        )}
      </div>
    </>
  )
}
